import {concat, emptyArray} from '@packages/utilities/fp/utils';
import {Component, Expressions, Types} from '@packages/systems/wfdl';
import {getName} from '@packages/systems/wfdl-Qualified';
import {EElement, type ExpressionElement} from '@packages/systems/wfdl-ast';
import type {TranslatedNodeData} from '@packages/systems/localization-types';
import {reservedImageAltValues} from './constants';
import {extractTranslatableDataFromExpression} from './extractTranslatableDataFromExpression';

/**
 * Given the instance element Expression (type `[__SitePlugin, rootElementId]`) of a component with properties,
 * if any overrides exist for its properties, those are returned shaped as nodes to translate.
 * Includes overrides that are lists of Elements and Texts (image's alt attributes).
 */
export function getTranslatableComponentPropertiesOverrides(
  componentInstance: ExpressionElement,
  componentPropertiesIds: string[],
  components: Component.ComponentMap
): TranslatedNodeData[] {
  const component = components.get(componentInstance.type);
  if (!component) return emptyArray;

  const componentId = getName(componentInstance.type);

  const componentProperties = Types.getRecordFields(
    Component.getDataType(component)
  );
  if (!componentProperties) return emptyArray;

  return componentPropertiesIds.reduce<TranslatedNodeData[]>(
    (overridesToTranslate, componentPropertyId) => {
      const overridesValues = Expressions.getIn(
        Expressions.getElementData(componentInstance),
        [{in: 'Record', at: componentPropertyId}]
      );

      if (!overridesValues) return overridesToTranslate;

      const propertyType = componentProperties[componentPropertyId];

      if (propertyType && Types.isAltTextType(propertyType)) {
        const value = Expressions.getText(overridesValues);
        if (value && !reservedImageAltValues.includes(value)) {
          const componentInstanceId =
            Expressions.getElementId(componentInstance);
          overridesToTranslate.push({
            _id: componentInstanceId,
            type: 'attr-property-override',
            text: true,
            v: value,
            fieldId: componentPropertyId,
            translatableParentId: componentInstanceId,
            componentId,
          });
        }
        return overridesToTranslate;
      }

      // Other overrides that are just texts like custom attributes are not handled yet.
      if (Expressions.isText(overridesValues)) {
        return overridesToTranslate;
      }

      // Properties that reference typographic Elements (p, h1, h2...) are a List of Text Elements.
      const overridesElementsList = Expressions.getList(overridesValues);

      if (!overridesElementsList) return overridesToTranslate;

      for (let i = 0; i < overridesElementsList.length; i++) {
        const overrideElementExpression = overridesElementsList[i];

        if (!overrideElementExpression) continue;

        const overrideElement = Expressions.getElement(
          overrideElementExpression
        );

        if (!overrideElement) continue;

        const translatedElementData = Expressions.everything<
          TranslatedNodeData[]
        >(concat)(
          extractTranslatableDataFromExpression(components)(overrideElement)(
            componentPropertyId
          )(componentId)
        )(EElement(overrideElement));
        overridesToTranslate.push(...translatedElementData);
      }

      return overridesToTranslate;
    },
    []
  );
}
